import { get } from 'lodash';
import {
	extensionUninstalling,
	setExtensionVersionAction,
} from 'Pages/NewFloContainer/NewFloExtensionToApp.actions';
import store from '../store';
import { setExtensionVersion } from '../store/reducers/OnBoarding.reducer';
import { doc360ScreenshotMinmumExtensionVersion } from './extensionVersion';
import {
	checkExtensionUninstalled,
	SendMessageToExtension,
} from './SendMessageToExtension';

const EXTENSION_CAPTURE_SCREENSHOT = 'floik/popup/extension-capture-screenshot';
const EXTENSION_ABORT_CAPTURE_SCREENSHOT =
	'floik/popup/extension-abort-capture-screenshot';
export const SEND_VERSION = 'floik-send-version';
export const SEND_CAPTURE_TAB_IMAGE = 'floik-capture-send';
export const SEND_CLOSED_TAB_ERROR = 'floik-screen-capture-tab-closed';

const activeStreams = new Map<string, MediaStream>();

export async function checkScreenShare(): Promise<MessageData> {
	try {
		let isExtensionInstalled = get(
			store?.getState(),
			'onBoarding.extensionInstalled',
			false
		);

		let extensionAttemptCount = 0;
		while (!isExtensionInstalled && extensionAttemptCount < 8) {
			await new Promise((resolve) => setTimeout(resolve, 1000)); // Sleep for delay milliseconds
			isExtensionInstalled = get(
				store?.getState(),
				'onBoarding.extensionInstalled',
				false
			);
			extensionAttemptCount++;
		}

		if (!isExtensionInstalled) {
			return {
				type: 'floik_app_extension_status',
				payload: {
					status: 'ERROR',
					value: false,
					message: 'Extension not found!',
				},
			};
		}

		let extensionVersion = get(store?.getState(), 'onBoarding.extensionVersion', 0.0);
		let attemptCount = 0;

		while (extensionVersion === 0 && attemptCount < 10) {
			await new Promise((resolve) => setTimeout(resolve, 1000)); // Sleep for delay milliseconds
			extensionVersion = get(store?.getState(), 'onBoarding.extensionVersion', 0.0);
			attemptCount++;
		}
		let validExtension =
			isExtensionInstalled && extensionVersion >= doc360ScreenshotMinmumExtensionVersion;

		if (validExtension) {
			return {
				type: 'floik_app_check_screenshot_feature',
				payload: {
					status: 'SUCCESS',
					value: true,
					message: extensionVersion,
				},
			};
		}

		return {
			type: 'floik_app_check_screenshot_feature',
			payload: {
				status: 'ERROR',
				value: false,
				message: 'Extension need to be upgraded!',
			},
		};
	} catch (error) {
		return {
			type: 'floik_app_check_screenshot_feature',
			payload: {
				status: 'ERROR',
				value: false,
				message: 'Extension need to be upgraded!',
			},
		};
	}
}

export async function abortCaptureScreenshot({
	requestId,
}: {
	requestId: string;
}): Promise<Boolean> {
	SendMessageToExtension({
		type: EXTENSION_ABORT_CAPTURE_SCREENSHOT,
		payload: {
			requestId: requestId,
		},
	});
	return true;
}

export async function invokeExtensionScreenShare({
	requestId,
}: {
	requestId: string;
}): Promise<MessageData> {
	try {
		//check if extension exist
		SendMessageToExtension({
			type: EXTENSION_CAPTURE_SCREENSHOT,
			payload: {
				requestId: requestId,
			},
		});
		return {
			type: 'floik_app_screenshot_image',
			payload: {
				status: 'INPROGRESS',
				requestId: requestId,
				show: true,
			},
		};
	} catch (error) {
		return {
			type: 'floik_app_screenshot_image',
			payload: {
				status: 'ERROR',
				requestId: requestId,
				message: 'Error communicating to extension!',
				errorCode: 'ERROR_COMMUNICATING_TO_EXTENSION',
			},
		};
	}
}

export async function invokeScreenShare({
	requestId,
}: {
	requestId: string;
}): Promise<MessageData> {
	const existingStream = activeStreams.get(requestId);

	if (existingStream) {
		return {
			type: 'floik_app_screenshot_image',
			payload: {
				status: 'IGNORED',
				message: 'Ignoring screen share as a request in progress for same requestId',
				requestId: requestId,
			},
		};
	}

	const constraints = {
		video: {
			logicalSurface: true,
			frameRate: {
				ideal: 24,
				max: 25,
			},
			resizeMode: 'none',
			aspectRatio: {
				ideal: 16 / 9,
			},
		},
		monitorTypeSurfaces: 'exclude',
		audio: false,
	};

	try {
		const stream = await navigator.mediaDevices.getDisplayMedia(constraints);

		if (!stream) {
			return {
				type: 'floik_app_screenshot_image',
				payload: {
					status: 'ERROR',
					message: 'Unexpected! stream is not available. Please check permissions',
					requestId: requestId,
					errorCode: 'ERROR_STREAM_NOT_FOUND',
				},
			};
		}

		const videoTrack = stream.getVideoTracks()[0];
		if (!videoTrack) {
			return {
				type: 'floik_app_screenshot_image',
				payload: {
					status: 'ERROR',
					message:
						'Unexpected! screenshare track not available. Please check permissions',
					requestId: requestId,
					errorCode: 'ERROR_TRACK_NOT_FOUND',
				},
			};
		}
		const settings = videoTrack.getSettings();
		const screenType = get(settings, 'displaySurface');

		activeStreams.set(requestId, stream);

		let shareType = 'unknown';
		if (screenType === 'browser') {
			return await invokeExtensionScreenShare({ requestId: requestId });
		} else if (screenType === 'window') {
			shareType = 'window';
			const capRes = await captureScreenshot(stream, requestId);
			SendMessageToExtension({ type: 'activateTab' });
			if (capRes) return capRes;
		} else if (screenType === 'monitor') {
			shareType = 'monitor';
		}

		return {
			type: 'floik_app_screenshot_image',
			payload: {
				status: 'ERROR',
				message: 'Unexpected!',
				requestId: requestId,
			},
		};
	} catch (error) {
		return {
			type: 'floik_app_screenshot_image',
			payload: {
				status: 'ERROR',
				message: 'Permission denied!',
				requestId: requestId,
				errorCode: 'ERROR_PERMISSION_DENIED',
			},
		};
	} finally {
		stopScreenShare(requestId);
	}
}

function stopScreenShare(requestId: string) {
	const streamData = activeStreams.get(requestId);
	if (streamData) {
		streamData.getTracks().forEach((track) => track.stop());
		activeStreams.delete(requestId);
	}
}

async function captureScreenshot(
	stream: MediaStream,
	requestId: string
): Promise<MessageData> {
	const videoTrack = stream.getVideoTracks()[0];
	const canvas = document.createElement('canvas');
	const { width, height } = videoTrack.getSettings();
	canvas.width = width || 0; // Provide default values to prevent errors
	canvas.height = height || 0;
	const context = canvas.getContext('2d');

	if (!context) {
		return {
			type: 'floik_app_screenshot_image',
			payload: {
				status: 'ERROR',
				message: '',
				requestId: requestId,
			},
		};
	}

	const videoElement = document.createElement('video');
	videoElement.srcObject = stream;
	videoElement.muted = true; // Prevent audio issues
	videoElement.playsInline = true;
	videoElement.autoplay = true;
	videoElement.style.display = 'none';
	let timeoutId;

	try {
		document.body.appendChild(videoElement);

		// videoElement.onloadedmetadata = () => {

		// 		videoElement.play();
		// 		const canvas = document.createElement('canvas');
		// 		canvas.width = videoElement.videoWidth;
		// 		canvas.height = videoElement.videoHeight;
		// 		const ctx = canvas.getContext('2d');
		// 		setTimeout(() => {
		// 			ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
		//             const imageData = canvas.toDataURL('image/png');
		// 			console.log("Captured Screenshot:", {'img':imageData});
		// 		},500);

		// }
		await videoElement.play(); // Ensure video is playing to capture a frame
		if (videoElement.paused || videoElement.ended) {
			return {
				type: 'floik_app_screenshot_image',
				payload: {
					status: 'ERROR',
					message: '',
					requestId: requestId,
				},
			}; // Exit the function if video failed to play
		}
		await new Promise((resolve) => setTimeout(resolve, 500));
		context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
		const pngDataUrl = canvas.toDataURL('image/png');

		return {
			type: 'floik_app_screenshot_image',
			payload: {
				status: 'SUCCESS',
				message: '',
				requestId: requestId,
				file: pngDataUrl,
			},
		};

		// return new Promise((resolve, reject) => {
		// 	timeoutId = setTimeout(() => {

		// 		context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
		// 		const pngDataUrl = canvas.toDataURL('image/png');
		// 	  // Resolve the promise with the desired value
		// 	  resolve({
		// 		type: 'floik_app_screenshot_image',
		// 		payload: {
		// 			status: 'INPROGRESS',
		// 			message: '',
		// 			requestId: requestId,
		// 			file: pngDataUrl,
		// 		},
		// 	});
		// 	}, 500);
		//   });
	} catch (error) {
		return {
			type: 'floik_app_screenshot_image',
			payload: {
				status: 'ERROR',
				message: '',
				requestId: requestId,
				errorCode: 'ERROR_CAPTURING_SCREEN',
			},
		};
	} finally {
		// Clean up: stop the video and remove it from the DOM
		videoElement.pause();
		videoElement.srcObject = null;
		videoElement.remove();
		if (timeoutId) clearTimeout(timeoutId);
	}
}

interface MessageData {
	type: string; // Add all possible types here
	payload?: any; // Optional payload for the message
}

export function handleMessage(event: Event) {
	try {
		const message: MessageData = event.data;

		switch (message.type) {
			case extensionUninstalling.type: {
				checkExtensionUninstalled();
				break;
			}
			case setExtensionVersionAction.type: {
				store.dispatch(setExtensionVersion(get(message.payload, 'version', '0.0.0')));
				break;
			}
			case SEND_CLOSED_TAB_ERROR: {
				window.doc360Instance?.onMessageFromMicroApp({
					type: 'floik_app_screenshot_image',
					payload: {
						status: 'ERROR',
						message: 'Tab closed!',
						requestId: get(message.payload, 'requestId'),
						errorCode: 'ERROR_USER_CLOSED_TAB',
					},
				});
				break;
			}
			case SEND_CAPTURE_TAB_IMAGE: {
				const status = get(message.payload, 'status', 'SUCCESS');
				let visibleTab;
				if (status === 'SUCCESS') {
					visibleTab = get(message.payload, 'visibleTab', true);
				}
				const payloadMsg = get(message.payload, 'message', '');
				window.doc360Instance?.onMessageFromMicroApp({
					type: 'floik_app_screenshot_image',
					payload: {
						status: status,
						message: payloadMsg,
						requestId: get(message.payload, 'requestId'),
						file: get(message.payload, 'file'),
						errorCode: status === 'ERROR' ? payloadMsg : null,
						visibleTab: visibleTab,
					},
				});
				SendMessageToExtension({ type: 'activateTab' });
				break;
			}
			default:
				// console.debug('discard unknown message type:', message.type);
				break;
		}
	} catch (error) {
	}
}

// Remove existing listener (if any):
if (handleMessage) {
	// When you want to remove the listener (e.g., when a component is unmounted in an SPA):
	window.removeEventListener('message', handleMessage, false);
}

window.addEventListener('message', handleMessage, false);
